import { useState } from "react";
import { Mail, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function EmailVerificationBanner() {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  // Don't show if user is verified, not logged in, or dismissed
  if (!user || user.email_verified !== false || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent! Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send verification email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3" data-testid="email-verification-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              Please verify your email address
            </p>
            <p className="text-xs text-amber-600/80 dark:text-amber-500/80">
              Check your inbox for the verification link. Some features may be limited until verified.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResend}
            disabled={sending}
            className="border-amber-500/30 text-amber-700 hover:bg-amber-500/10 dark:text-amber-400"
            data-testid="resend-verification-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-3 h-3 mr-1.5" />
                Resend Email
              </>
            )}
          </Button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded hover:bg-amber-500/10 text-amber-600"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
